import React from 'react';
import { getTranslations } from '../utils/translations';
// Generic fallback poster for audio ads when no companion image is available
const FALLBACK_IMAGE = window.broadcastPlayerData?.fallbackImageUrl || 'https://insightk.kinsta.cloud/wp-content/uploads/2025/04/broadcast-600x400.jpg';

const AudioAdUI = ({ currentPlaylistItem, dimensions, adRemainingTime, adDuration, isAdMuted, onToggleMute, companionPosterUrl, companionClickUrl, companionAltText, isSeries, isMobile, adSkipTimeOffset, onSkipAd, onAdClick }) => {
  const t = getTranslations();
  // Use companion URL first, then series poster, then generic fallback
  const adPoster = companionPosterUrl || currentPlaylistItem?.prerollAdPoster || FALLBACK_IMAGE;
  const isSinglePlayer = currentPlaylistItem?.playerType === 'single';
  const tooltipText = companionAltText || t.donateTooltip;

  const canSkip = typeof adSkipTimeOffset === 'number' && adSkipTimeOffset <= 0;
  const skipCountdown = typeof adSkipTimeOffset === 'number' && adSkipTimeOffset > 0 ? Math.ceil(adSkipTimeOffset) : null;

  const containerStyle = {
    position: 'absolute',
    top: isSeries ? (isSinglePlayer ? 0 : 25) : 20,
    left: isSeries ? (isMobile ? 10 : (isSinglePlayer ? 0 : 25)) : 20,
    width: dimensions ? `${dimensions.width}px` : '100%',
    height: dimensions ? `${dimensions.height}px` : '100%',
    backgroundColor: '#000',
    color: '#fff',
    zIndex: 101, // Higher than IMA container (99)
  };

  const imageStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    cursor: 'pointer',
    zIndex: 1,
  };

  const textStyle = {
    position: 'absolute',
    bottom: '10px',
    left: '50%',
    transform: 'translateX(-50%)',
    fontSize: '12px',
    fontWeight: '400',
    fontFamily: 'Arial, sans-serif',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: '#fff',
    padding: '5px 15px',
    borderRadius: '3px',
    margin: 0,
    pointerEvents: 'none', // Prevent text from blocking clicks on the image
    zIndex: 5,
  };

  const timerStyle = {
    position: 'absolute',
    top: '10px',
    left: '10px',
    fontSize: '12px',
    fontWeight: '400',
    fontFamily: 'Arial, sans-serif',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: '#fff',
    padding: '4px 8px',
    borderRadius: '3px',
    margin: 0,
    pointerEvents: 'none',
    zIndex: 5,
  };

  const skipButtonStyle = {
    position: 'absolute',
    bottom: '10px',
    right: '10px',
    fontSize: '12px',
    fontWeight: '600',
    fontFamily: 'Arial, sans-serif',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: '#fff',
    padding: '6px 12px',
    borderRadius: '3px',
    margin: 0,
    border: '1px solid rgba(255,255,255,0.6)',
    cursor: canSkip ? 'pointer' : 'default',
    pointerEvents: canSkip ? 'auto' : 'none',
    display: (canSkip || skipCountdown !== null) ? 'block' : 'none',
    zIndex: 10, // Ensure it's above the image
  };

  const muteButtonStyle = {
    position: 'absolute',
    bottom: '10px',
    left: '10px',
    fontSize: '12px',
    fontWeight: '600',
    fontFamily: 'Arial, sans-serif',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: '#fff',
    padding: '6px 10px',
    borderRadius: '3px',
    margin: 0,
    border: '1px solid rgba(255,255,255,0.6)',
    cursor: 'pointer',
    zIndex: 10, // Ensure it's above the image
  };

  const handleAdClick = () => {
    // Fire analytics before opening the new tab — popup blockers shouldn't cost us the event.
    if (typeof onAdClick === 'function') onAdClick();
    const targetUrl = companionClickUrl || window.broadcastPlayerData?.pauseAdClickUrl || 'https://give.insight.org/donate/give?source=WEB25SPAD';
    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div id="audio-ad-container" style={containerStyle}>
      {typeof adRemainingTime === 'number' && adRemainingTime >= 0 && (
        <p style={timerStyle}>
          {t.ad}{Math.floor(adRemainingTime)}s
        </p>
      )}
      {adPoster && <img src={adPoster} alt="Advertisement" title={tooltipText} style={imageStyle} onClick={handleAdClick} />}
      {typeof onToggleMute === 'function' && (
        <div
          role="button"
          aria-pressed={!isAdMuted}
          tabIndex={0}
          style={muteButtonStyle}
          onClick={onToggleMute}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onToggleMute();
            }
          }}
          className="audio-ad-mute"
        >
          {isAdMuted ? t.unmute : t.mute}
        </div>
      )}

      {(canSkip || skipCountdown !== null) && (
        <div
          role="button"
          tabIndex={canSkip ? 0 : -1}
          style={skipButtonStyle}
          onClick={canSkip ? onSkipAd : undefined}
          onKeyDown={(e) => {
            if (canSkip && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              onSkipAd();
            }
          }}
          className="audio-ad-skip"
        >
          {canSkip ? t.skipAd : `${t.skipAdIn}${skipCountdown}s`}
        </div>
      )}
      
      <p id="advertisement-text" style={textStyle}>{t.advertisement}</p>
    </div>
  );
};

export default AudioAdUI;
